import { useState, useEffect } from "react";
import "./ConfluencePagesPicker.css";
import Modal from "../shared/Modal";
import { DriveResource, MSDrive } from "winslow-core";
import { selectUser } from "../../redux/features/appData";
import { useSelector } from "react-redux";
import { MULTI_SELECT_KEY } from "../folder-picker/FolderPicker";
import { apiGetConfluenceSpacePages } from "../../services/DriveService";

type ConfluencePagesPickerProps = {
  isOpen: boolean;
  onClose: () => void;
  onSelected: (pages: DriveResource[]) => void;
  confluenceSpace: MSDrive | null;
  handleOpenConfluencePicker: () => void;
};

export const ConfluencePagesPicker = ({
  isOpen,
  onClose,
  onSelected,
  confluenceSpace,
  handleOpenConfluencePicker,
}: ConfluencePagesPickerProps) => {
  const storeUser = useSelector(selectUser);
  const [multiKeyDown, setMultiKeyDown] = useState<boolean>(false);
  const [selectedPages, setSelectedPages] = useState<string[]>([]);
  const [pages, setPages] = useState<DriveResource[]>([]);

  console.log("[ConfluencePagesPicker] confluenceSpace: ", confluenceSpace);

  useEffect(() => {
    (async () => {
      if (!storeUser || !confluenceSpace) {
        return;
      }

      const pagesResults = await apiGetConfluenceSpacePages({
        userId: storeUser.id,
        spaceId: confluenceSpace.id,
      });
      console.log("[ConfluencePagesPicker] pagesResults: ", {
        confluenceSpace,
        pagesResults,
      });
      setPages(pagesResults);
    })();

    window.addEventListener("keydown", handleGlobalKeyDown);
    window.addEventListener("keyup", handleGlobalKeyUp);

    return () => {
      window.removeEventListener("keydown", handleGlobalKeyDown);
      window.removeEventListener("keyup", handleGlobalKeyUp);
      setPages([]);
    };
  }, [confluenceSpace]);

  const handleGlobalKeyDown = (e: any) => {
    if (e.key === MULTI_SELECT_KEY) {
      setMultiKeyDown(true);
    }

    if (!multiKeyDown && (e.key === "a" || e.key === "A")) {
      selectAllPages();
      e.stopPropagation();
      e.preventDefault();
    }
  };

  const handleGlobalKeyUp = (e: any) => {
    if (e.key === MULTI_SELECT_KEY) {
      setMultiKeyDown(false);
    }
  };

  const pageIsSelected = (id: string) => {
    return selectedPages.findIndex((pageId: string) => pageId === id) !== -1;
  };

  const selectPage = (id: string) => {
    const newSelectedPages = [...selectedPages];
    newSelectedPages.push(id);
    setSelectedPages(newSelectedPages);
  };

  const unselectPage = (id: string) => {
    const index = selectedPages.findIndex((pageId: string) => pageId === id);

    if (index !== -1) {
      const newSelectedPages = [
        ...selectedPages.slice(0, index),
        ...selectedPages.slice(index + 1),
      ];
      setSelectedPages(newSelectedPages);
    }
  };

  const resetSelectedPagesToOne = (pageId: string) => {
    setSelectedPages([pageId]);
  };

  const selectAllPages = () => {
    if (!confluenceSpace) {
      return;
    }

    setSelectedPages(pages.map((page) => page.id));
  };

  const handleSelectPages = () => {
    if (!confluenceSpace) {
      return;
    }

    const finalDriveResources: DriveResource[] = [];

    selectedPages.forEach((pageId: string) => {
      if (!pages) {
        return;
      }

      const index = pages.findIndex(
        (page: DriveResource) => page.id === pageId
      );

      if (index !== -1) {
        finalDriveResources.push({ ...pages[index] });
      }
    });

    onSelected(finalDriveResources);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      style={null}
      onClose={onClose}
      canClose={false}
      allowClickout={true}>
      <div className="confluence-pages-picker-container">
        {confluenceSpace && (
          <>
            <div className="confluence-pages-picker-header">
              <div className="confluence-pages-picker-title">
                Pick Pages from: {confluenceSpace.name}
              </div>
              <div className="confluence-pages-picker-buttons">
                <div className="confluence-pages-picker-select">
                  <button onClick={handleSelectPages}>Select</button>
                </div>
                <div className="confluence-pages-picker-close">
                  <img
                    src="/icons/close.svg"
                    alt="Close"
                    className="icon-close"
                    onClick={onClose}
                  />
                </div>
              </div>
            </div>
            <div className="confluence-pages-picker-back">
              <div className="confluence-pages-picker-back-container">
                <div
                  className="confluence-pages-picker-arrow"
                  onClick={handleOpenConfluencePicker}>
                  <img src="/icons/arrow_left.png" alt="Back" />
                </div>
              </div>
            </div>
            <div className="confluence-pages-picker-divider"></div>

            <div className="confluence-pages-picker-body">
              {pages
                ? [...pages]
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map((page: DriveResource) => (
                      <div
                        key={`page-${page.id}`}
                        className={
                          pageIsSelected(page.id)
                            ? "confluence-pages-picker-page-selected"
                            : "confluence-pages-picker-page"
                        }
                        onClick={() => {
                          if (!multiKeyDown) {
                            resetSelectedPagesToOne(page.id);
                            return;
                          }

                          if (pageIsSelected(page.id)) {
                            unselectPage(page.id);
                          } else {
                            selectPage(page.id);
                          }
                        }}>
                        <div className="confluence-pages-picker-icon">
                          <img
                            src="/icons/confluence.svg"
                            alt="Confluence page"
                          />
                        </div>
                        <div className="confluence-pages-picker-name">
                          {page.name}
                        </div>
                      </div>
                    ))
                : null}
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default ConfluencePagesPicker;
